/**
 * UserDropdownMenu Component
 * Dropdown menu with user settings, profile, and admin access
 */
import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { useLocation } from 'wouter-preact';
import { currentUser, isAdmin } from '../../state/userState.js';
import { logout } from '../../services/authService.js';
import { trackEvent } from '../../modules/dataService.js';

export function UserDropdownMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [location, setLocation] = useLocation();
  const dropdownRef = useRef(null);
  const user = currentUser.value;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close dropdown on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  if (!user) {
    return (
      <button 
        className="cmdbar-btn" 
        onClick={() => setLocation('/login')}
        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M13.8 12H3"/>
        </svg>
        Login
      </button>
    );
  }

  const handleLogout = async () => {
    trackEvent('Logout', { method: 'menu' });
    await logout();
    setLocation('/login');
  };

  const menuItems = [
    {
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      ),
      label: 'Profile',
      onClick: () => setLocation('/profile'),
    },
    {
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 6v6l4 2"/>
        </svg>
      ),
      label: 'Subscription',
      onClick: () => setLocation('/subscription'),
    },
  ];

  // Add admin portal link for superusers
  if (isAdmin()) {
    menuItems.push({
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
        </svg>
      ),
      label: 'Admin Portal',
      onClick: () => setLocation('/admin'),
      highlight: true,
    });
  }

  menuItems.push({
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
      </svg>
    ),
    label: 'Logout',
    onClick: handleLogout,
    danger: true,
  });

  return (
    <div className="user-dropdown-container" ref={dropdownRef}>
      <button
        className={`user-dropdown-trigger${isOpen ? ' user-dropdown-trigger--open' : ''}`}
        // The open-state corner flattening is ALSO set inline, not only via the
        // --open class. The class is applied (verified in the browser) and its
        // rule is in the shipped stylesheet, yet the computed
        // border-bottom-left-radius stayed 24px — and did not budge even for a
        // higher-specificity `!important` override injected at runtime, so it is
        // not an ordinary cascade loss. I could not explain it under time
        // pressure and chose a route that cannot lose over one that provably
        // does. If someone later works out what wins, delete this and keep the
        // class; the CSS rule is still there and still correct.
        style={isOpen ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : undefined}
        onClick={() => setIsOpen(!isOpen)}
        title={user.full_name || user.email}
      >
        {/* User Avatar Circle */}
        <div className="user-avatar">
          {(() => {
            const f = String(user.first_name || '').charAt(0);
            const l = String(user.last_name || '').charAt(0);
            if (f && l) return (f + l).toUpperCase();
            return (f || String(user.email || '').charAt(0) || 'U').toUpperCase();
          })()}
        </div>
        
        {/* User Name */}
        <span className="user-name">
          {[user.first_name, user.last_name].filter(Boolean).join(' ') || String(user.email || '').split('@')[0]}
        </span>
        
        {/* Dropdown Arrow */}
        <svg 
          className="dropdown-arrow" 
          width="14" 
          height="14" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
        >
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="user-dropdown-menu">
          {/* User Info Header */}
          <div className="dropdown-header">
            <div className="user-info-large">
              <div className="user-avatar-large">
                {(() => {
                  const f = String(user.first_name || '').charAt(0);
                  const l = String(user.last_name || '').charAt(0);
                  if (f && l) return (f + l).toUpperCase();
                  return (f || String(user.email || '').charAt(0) || 'U').toUpperCase();
                })()}
              </div>
              <div className="user-details">
                <div className="user-full-name">
                  {[user.title, user.first_name, user.last_name].filter(Boolean).join(' ') || String(user.email || 'N/A')}
                </div>
                <div className="user-email">{String(user.email || 'N/A')}</div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="dropdown-divider"></div>

          {/* Menu Items */}
          {menuItems.map((item, index) => (
            <button
              key={index}
              className={`dropdown-item ${item.danger ? 'dropdown-item--danger' : ''} ${item.highlight ? 'dropdown-item--highlight' : ''}`}
              onClick={item.onClick}
            >
              <span className="dropdown-item-icon">{item.icon}</span>
              <span className="dropdown-item-label">{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
